// Procedural canvas textures for the city. Zero external assets.
import * as THREE from 'three';
import { makeRng, type Rng } from './util';

export interface CanvasCtx {
  c: HTMLCanvasElement;
  g: CanvasRenderingContext2D;
  w: number;
  h: number;
}

export function canvas(w: number, h: number): CanvasCtx {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  return { c, g, w, h };
}

export function toTexture(cc: CanvasCtx, opts: { srgb?: boolean; repeat?: boolean; aniso?: number } = {}): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(cc.c);
  if (opts.srgb !== false) t.colorSpace = THREE.SRGBColorSpace;
  if (opts.repeat !== false) {
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
  } else {
    t.wrapS = THREE.ClampToEdgeWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;
  }
  t.anisotropy = opts.aniso ?? 8;
  t.needsUpdate = true;
  return t;
}

/** per-pixel (scale 1) or blocky luminance noise, in place */
export function noise(cc: CanvasCtx, amount: number, rng: Rng, scale = 1): void {
  const { g, w, h } = cc;
  const img = g.getImageData(0, 0, w, h);
  const d = img.data;
  if (scale <= 1) {
    for (let i = 0; i < d.length; i += 4) {
      const n = (rng() - 0.5) * 2 * amount;
      d[i] += n;
      d[i + 1] += n;
      d[i + 2] += n;
    }
  } else {
    for (let y = 0; y < h; y += scale) {
      for (let x = 0; x < w; x += scale) {
        const n = (rng() - 0.5) * 2 * amount;
        const ye = Math.min(h, y + scale);
        const xe = Math.min(w, x + scale);
        for (let yy = y; yy < ye; yy++) {
          let i = (yy * w + x) * 4;
          for (let xx = x; xx < xe; xx++) {
            d[i] += n;
            d[i + 1] += n;
            d[i + 2] += n;
            i += 4;
          }
        }
      }
    }
  }
  g.putImageData(img, 0, 0);
}

const rgb = (r: number, g: number, b: number) => `rgb(${r | 0},${g | 0},${b | 0})`;

// ------------------------------------------------------------------ facades ----

export interface FacadeStyle {
  key: string;
  map: THREE.Texture;
  emissive: THREE.Texture;
  /** metres covered by one texture tile */
  tileW: number;
  tileH: number;
  floorH: number;
  bayW: number;
  bays: number;
  floors: number;
  /** vertex-colour tints (linear multipliers) */
  tints: number[];
}

interface FacadeSpec {
  key: string;
  bays: number;
  floors: number;
  bayW: number;
  floorH: number;
  wall: [number, number, number];
  frame: string;
  glass: string;
  winW: number;
  winH: number;
  winTop: 'flat' | 'round' | 'point';
  sill?: boolean;
  band?: [number, number, number] | null;
  pilaster?: boolean;
  brick?: boolean;
  panel?: boolean;
  curtain?: boolean;
  balcony?: number;
  litP: number;
  tints: number[];
  ornament?: boolean;
}

function drawWindowShape(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, top: 'flat' | 'round' | 'point'): void {
  g.beginPath();
  if (top === 'flat') {
    g.rect(x, y, w, h);
  } else if (top === 'round') {
    const r = w / 2;
    g.moveTo(x, y + h);
    g.lineTo(x, y + r);
    g.arc(x + r, y + r, r, Math.PI, 0);
    g.lineTo(x + w, y + h);
    g.closePath();
  } else {
    const r = w / 2;
    g.moveTo(x, y + h);
    g.lineTo(x, y + r * 1.1);
    g.quadraticCurveTo(x + r * 0.35, y + r * 0.25, x + r, y);
    g.quadraticCurveTo(x + w - r * 0.35, y + r * 0.25, x + w, y + r * 1.1);
    g.lineTo(x + w, y + h);
    g.closePath();
  }
}

function makeFacade(spec: FacadeSpec, rng: Rng): FacadeStyle {
  const tileW = spec.bays * spec.bayW;
  const tileH = spec.floors * spec.floorH;
  const W = 1024;
  const H = Math.round((W * tileH) / tileW / 64) * 64 || 512;
  const cc = canvas(W, H);
  const ec = canvas(W, H);
  const { g } = cc;
  const eg = ec.g;
  const px = W / tileW; // px per metre horizontally
  const py = H / tileH;
  const [wr, wg, wb] = spec.wall;
  g.fillStyle = rgb(wr, wg, wb);
  g.fillRect(0, 0, W, H);
  eg.fillStyle = '#000';
  eg.fillRect(0, 0, W, H);

  if (spec.brick) {
    const bh = 0.09 * py;
    const bw = 0.25 * px;
    for (let y = 0; y < H; y += bh) {
      const row = Math.round(y / bh);
      for (let x = -bw; x < W; x += bw) {
        const xx = x + (row % 2) * bw * 0.5;
        const v = (rng() - 0.5) * 26;
        g.fillStyle = rgb(wr + v, wg + v * 0.8, wb + v * 0.6);
        g.fillRect(xx + 1, y + 1, bw - 2, bh - 1.5);
      }
    }
    g.fillStyle = 'rgba(0,0,0,0.12)';
    for (let y = 0; y < H; y += bh) g.fillRect(0, y, W, 1);
  }
  if (spec.panel) {
    g.strokeStyle = rgb(wr - 30, wg - 30, wb - 30);
    g.lineWidth = 2;
    for (let f = 0; f <= spec.floors; f++) {
      const y = H - f * spec.floorH * py;
      g.beginPath();
      g.moveTo(0, y);
      g.lineTo(W, y);
      g.stroke();
    }
    for (let b = 0; b <= spec.bays; b++) {
      const x = b * spec.bayW * px;
      g.beginPath();
      g.moveTo(x, 0);
      g.lineTo(x, H);
      g.stroke();
    }
  }
  if (spec.curtain) {
    // glass curtain wall: every floor = spandrel band + glass band, mullions per bay
    for (let f = 0; f < spec.floors; f++) {
      const y1 = H - f * spec.floorH * py;
      const y0 = y1 - spec.floorH * py;
      const spH = spec.floorH * 0.28 * py;
      // glass
      const grad = g.createLinearGradient(0, y0, 0, y1);
      grad.addColorStop(0, spec.glass);
      grad.addColorStop(1, rgb(wr, wg, wb));
      g.fillStyle = grad;
      g.fillRect(0, y0, W, y1 - y0 - spH);
      // spandrel
      g.fillStyle = rgb(wr * 0.55, wg * 0.55, wb * 0.6);
      g.fillRect(0, y1 - spH, W, spH);
      g.fillStyle = 'rgba(255,255,255,0.18)';
      g.fillRect(0, y1 - spH, W, 2);
      // lit runs at night
      let b = 0;
      while (b < spec.bays) {
        const run = 1 + Math.floor(rng() * 4);
        if (rng() < spec.litP) {
          const k = 0.55 + rng() * 0.45;
          eg.fillStyle = rgb(255 * k, 235 * k, 200 * k);
          eg.fillRect(b * spec.bayW * px + 2, y0 + 2, run * spec.bayW * px - 4, y1 - y0 - spH - 4);
        }
        b += run;
      }
    }
    // mullions
    g.fillStyle = 'rgba(20,24,30,0.9)';
    for (let b = 0; b <= spec.bays; b++) g.fillRect(b * spec.bayW * px - 1.5, 0, 3, H);
    for (let f = 0; f <= spec.floors; f++) g.fillRect(0, H - f * spec.floorH * py - 1, W, 2);
    noise(cc, 5, rng);
    return { key: spec.key, map: toTexture(cc), emissive: toTexture(ec), tileW, tileH, floorH: spec.floorH, bayW: spec.bayW, bays: spec.bays, floors: spec.floors, tints: spec.tints };
  }

  noise(cc, spec.brick ? 8 : 9, rng, spec.brick ? 1 : 2);
  // pilasters between bays
  if (spec.pilaster) {
    for (let b = 0; b <= spec.bays; b++) {
      const x = b * spec.bayW * px;
      g.fillStyle = rgb(wr + 14, wg + 14, wb + 12);
      g.fillRect(x - 0.22 * px, 0, 0.44 * px, H);
      g.fillStyle = 'rgba(0,0,0,0.18)';
      g.fillRect(x + 0.2 * px, 0, 3, H);
      g.fillStyle = 'rgba(255,255,255,0.18)';
      g.fillRect(x - 0.22 * px, 0, 3, H);
    }
  }
  // floor bands
  if (spec.band) {
    const [br, bg, bb] = spec.band;
    const bh = 0.28 * py;
    for (let f = 0; f <= spec.floors; f++) {
      const y = H - f * spec.floorH * py;
      g.fillStyle = rgb(br, bg, bb);
      g.fillRect(0, y - bh, W, bh);
      g.fillStyle = 'rgba(0,0,0,0.22)';
      g.fillRect(0, y - 2, W, 2);
      g.fillStyle = 'rgba(255,255,255,0.22)';
      g.fillRect(0, y - bh, W, 2);
    }
  }
  if (spec.ornament) {
    for (let f = 0; f < spec.floors; f++) {
      const y = H - f * spec.floorH * py - 0.45 * py;
      g.fillStyle = 'rgba(60,90,70,0.55)';
      for (let x = 0; x < W; x += 0.5 * px) {
        g.beginPath();
        g.arc(x + 0.25 * px, y, 0.08 * px, 0, Math.PI * 2);
        g.fill();
      }
    }
  }
  // windows
  const ww = spec.winW * px;
  const wh = spec.winH * py;
  for (let f = 0; f < spec.floors; f++) {
    const y1 = H - f * spec.floorH * py; // floor bottom
    const wy = y1 - spec.floorH * py + (spec.floorH - spec.winH) * 0.55 * py; // window top
    for (let b = 0; b < spec.bays; b++) {
      const wx = b * spec.bayW * px + (spec.bayW - spec.winW) * 0.5 * px;
      const hasBalcony = spec.balcony && rng() < spec.balcony;
      // frame (slightly larger)
      g.fillStyle = spec.frame;
      drawWindowShape(g, wx - 0.08 * px, wy - 0.08 * py, ww + 0.16 * px, wh + 0.16 * py, spec.winTop);
      g.fill();
      // shadow at the top (reveal)
      g.fillStyle = 'rgba(0,0,0,0.35)';
      drawWindowShape(g, wx, wy, ww, wh, spec.winTop);
      g.fill();
      // glass
      const grad = g.createLinearGradient(0, wy, 0, wy + wh);
      grad.addColorStop(0, spec.glass);
      grad.addColorStop(1, 'rgba(120,140,160,1)');
      g.fillStyle = grad;
      drawWindowShape(g, wx + 2, wy + 3, ww - 4, wh - 5, spec.winTop);
      g.fill();
      // cross bars
      g.fillStyle = spec.frame;
      g.fillRect(wx + ww / 2 - 1, wy + wh * 0.25, 2, wh * 0.75);
      g.fillRect(wx, wy + wh * 0.42, ww, 2);
      if (spec.sill) {
        g.fillStyle = 'rgba(255,255,255,0.45)';
        g.fillRect(wx - 0.14 * px, wy + wh + 0.06 * py, ww + 0.28 * px, 0.08 * py);
        g.fillStyle = 'rgba(0,0,0,0.28)';
        g.fillRect(wx - 0.14 * px, wy + wh + 0.14 * py, ww + 0.28 * px, 0.05 * py);
      }
      if (hasBalcony) {
        const bx = wx - 0.35 * px;
        const bw = ww + 0.7 * px;
        const by = wy + wh - 0.2 * py;
        g.fillStyle = 'rgba(0,0,0,0.3)';
        g.fillRect(bx, by, bw, 0.9 * py);
        g.strokeStyle = 'rgba(40,40,45,0.9)';
        g.lineWidth = 2;
        for (let i = 0; i <= 6; i++) {
          const x = bx + (bw * i) / 6;
          g.beginPath();
          g.moveTo(x, by);
          g.lineTo(x, by + 0.9 * py);
          g.stroke();
        }
        g.fillStyle = rgb(wr - 20, wg - 20, wb - 20);
        g.fillRect(bx - 2, by + 0.9 * py, bw + 4, 0.1 * py);
      }
      // emissive
      if (rng() < spec.litP) {
        const k = 0.5 + rng() * 0.5;
        const warm = rng() < 0.8;
        eg.fillStyle = warm ? rgb(255 * k, 214 * k, 150 * k) : rgb(170 * k, 200 * k, 255 * k);
        drawWindowShape(eg, wx + 2, wy + 3, ww - 4, wh - 5, spec.winTop);
        eg.fill();
        if (rng() < 0.4) {
          eg.fillStyle = 'rgba(0,0,0,0.5)';
          eg.fillRect(wx + 2, wy + 3, ww - 4, (wh - 5) * (0.3 + rng() * 0.5));
        }
      }
    }
  }
  return { key: spec.key, map: toTexture(cc), emissive: toTexture(ec), tileW, tileH, floorH: spec.floorH, bayW: spec.bayW, bays: spec.bays, floors: spec.floors, tints: spec.tints };
}

// ------------------------------------------------------------ shop fronts ----

const SHOP_NAMES = ['ABC', 'PÉKSÉG', 'TRAFIK', 'KÁVÉZÓ', 'FODRÁSZ', 'ZÖLDSÉG', 'GYÓGYSZERTÁR', 'BISZTRÓ', 'CUKRÁSZDA', 'SÖRÖZŐ', 'BOLT', 'ÉTTEREM', 'OPTIKA', 'VIRÁG', 'BANK', 'HOTEL', 'PIZZA', 'KEBAB', 'LÁNGOS', 'BÜFÉ'];
const SHOP_SIGN_COLORS = ['#8b1e2d', '#1e3a5f', '#2d5a27', '#b8860b', '#222222', '#5b2a86', '#c0392b', '#0f6b6b', '#d35400'];

export function makeShopStrip(rng: Rng): FacadeStyle {
  const shops = 8;
  const shopW = 5;
  const tileW = shops * shopW;
  const tileH = 4.5;
  const W = 2048;
  const H = 256;
  const cc = canvas(W, H);
  const ec = canvas(W, H);
  const { g } = cc;
  const eg = ec.g;
  const px = W / tileW;
  const py = H / tileH;
  eg.fillStyle = '#000';
  eg.fillRect(0, 0, W, H);
  for (let s = 0; s < shops; s++) {
    const x0 = s * shopW * px;
    const wv = 150 + rng() * 60;
    g.fillStyle = rgb(wv, wv * 0.96, wv * 0.9);
    g.fillRect(x0, 0, shopW * px, H);
    // sign band
    const signH = 0.75 * py;
    const signY = 0.35 * py;
    const col = SHOP_SIGN_COLORS[Math.floor(rng() * SHOP_SIGN_COLORS.length)];
    g.fillStyle = col;
    g.fillRect(x0 + 0.2 * px, signY, shopW * px - 0.4 * px, signH);
    g.fillStyle = '#f5efe0';
    g.font = `bold ${Math.round(signH * 0.62)}px sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const name = SHOP_NAMES[Math.floor(rng() * SHOP_NAMES.length)];
    g.fillText(name, x0 + (shopW * px) / 2, signY + signH * 0.52);
    // window + door
    const winY = signY + signH + 0.15 * py;
    const winH = H - winY - 0.25 * py;
    const doorW = 1.1 * px;
    const doorX = rng() < 0.5 ? x0 + 0.35 * px : x0 + shopW * px - 0.35 * px - doorW;
    const winX = doorX === x0 + 0.35 * px ? doorX + doorW + 0.2 * px : x0 + 0.35 * px;
    const winW = shopW * px - 0.7 * px - doorW - 0.2 * px;
    // frame
    g.fillStyle = '#2a2a2e';
    g.fillRect(winX - 4, winY - 4, winW + 8, winH + 8);
    const grad = g.createLinearGradient(0, winY, 0, winY + winH);
    grad.addColorStop(0, '#5b6b7a');
    grad.addColorStop(0.5, '#33404c');
    grad.addColorStop(1, '#232a33');
    g.fillStyle = grad;
    g.fillRect(winX, winY, winW, winH);
    // reflections
    g.fillStyle = 'rgba(255,255,255,0.10)';
    g.fillRect(winX + winW * 0.1, winY, winW * 0.08, winH);
    g.fillRect(winX + winW * 0.55, winY, winW * 0.04, winH);
    // door
    g.fillStyle = '#2a2a2e';
    g.fillRect(doorX - 3, winY - 4, doorW + 6, H - winY + 4);
    g.fillStyle = rng() < 0.5 ? '#4a3b2a' : '#3a4a5a';
    g.fillRect(doorX, winY, doorW, H - winY);
    g.fillStyle = 'rgba(200,220,240,0.5)';
    g.fillRect(doorX + doorW * 0.2, winY + winH * 0.1, doorW * 0.6, winH * 0.5);
    // awning stripe band (drawn) for some shops
    if (rng() < 0.4) {
      const ac = rng() < 0.5 ? '#8b1e2d' : '#2d5a27';
      g.fillStyle = ac;
      g.fillRect(x0 + 0.15 * px, signY + signH + 0.02 * py, shopW * px - 0.3 * px, 0.14 * py);
      g.fillStyle = 'rgba(255,255,255,0.35)';
      for (let x = x0 + 0.15 * px; x < x0 + shopW * px - 0.3 * px; x += 0.5 * px) g.fillRect(x, signY + signH + 0.02 * py, 0.25 * px, 0.14 * py);
    }
    // emissive: window lit, sign lit
    const lit = rng() < 0.85;
    if (lit) {
      const warm = rng() < 0.7;
      const k = 0.6 + rng() * 0.4;
      eg.fillStyle = warm ? rgb(255 * k, 225 * k, 170 * k) : rgb(200 * k, 235 * k, 255 * k);
      eg.fillRect(winX, winY, winW, winH);
      eg.fillRect(doorX + doorW * 0.2, winY + winH * 0.1, doorW * 0.6, winH * 0.5);
      eg.fillStyle = 'rgba(0,0,0,0.35)';
      eg.fillRect(winX, winY + winH * 0.6, winW, winH * 0.4);
    }
    eg.fillStyle = 'rgba(255,255,255,0.75)';
    eg.font = g.font;
    eg.textAlign = 'center';
    eg.textBaseline = 'middle';
    eg.fillText(name, x0 + (shopW * px) / 2, signY + signH * 0.52);
    // pilaster between shops
    g.fillStyle = 'rgba(0,0,0,0.25)';
    g.fillRect(x0, 0, 4, H);
  }
  noise(cc, 6, rng, 2);
  return { key: 'shop', map: toTexture(cc), emissive: toTexture(ec), tileW, tileH, floorH: 4.5, bayW: 5, bays: 8, floors: 1, tints: [0xffffff] };
}

// -------------------------------------------------------------- surfaces ----

export function makeAsphaltRoad(rng: Rng): THREE.Texture {
  // 14 m wide (u) x 8 m long (v). dashes: 3 m on / 5 m off.
  const W = 512;
  const H = 288;
  const cc = canvas(W, H);
  const { g } = cc;
  g.fillStyle = rgb(58, 58, 61);
  g.fillRect(0, 0, W, H);
  noise(cc, 8, rng, 1);
  noise(cc, 6, rng, 6);
  // lane wear
  for (const u of [0.25, 0.75]) {
    const grad = g.createLinearGradient((u - 0.12) * W, 0, (u + 0.12) * W, 0);
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(0.35, 'rgba(0,0,0,0.13)');
    grad.addColorStop(0.5, 'rgba(0,0,0,0.05)');
    grad.addColorStop(0.65, 'rgba(0,0,0,0.13)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect((u - 0.12) * W, 0, 0.24 * W, H);
  }
  const pxm = W / 14;
  // centre dashes
  g.fillStyle = 'rgba(225,225,215,0.85)';
  g.fillRect(W / 2 - 0.08 * pxm, 0, 0.16 * pxm, (3 / 8) * H);
  // edge lines
  g.fillStyle = 'rgba(215,215,205,0.7)';
  g.fillRect((0.5 - 6.55 / 14) * W - 1.5, 0, 3, H);
  g.fillRect((0.5 + 6.55 / 14) * W - 1.5, 0, 3, H);
  // cracks
  g.strokeStyle = 'rgba(0,0,0,0.25)';
  g.lineWidth = 1.5;
  for (let i = 0; i < 6; i++) {
    g.beginPath();
    let x = rng() * W;
    let y = rng() * H;
    g.moveTo(x, y);
    for (let k = 0; k < 6; k++) {
      x += (rng() - 0.5) * 40;
      y += (rng() - 0.5) * 40;
      g.lineTo(x, y);
    }
    g.stroke();
  }
  return toTexture(cc);
}

export function makeAsphaltPlain(rng: Rng): THREE.Texture {
  const cc = canvas(512, 512);
  cc.g.fillStyle = rgb(58, 58, 61);
  cc.g.fillRect(0, 0, 512, 512);
  noise(cc, 8, rng, 1);
  noise(cc, 6, rng, 8);
  return toTexture(cc);
}

export function makeCrosswalk(rng: Rng): THREE.Texture {
  // 14 m (u) x 3 m (v), stripes across u
  const W = 512;
  const H = 128;
  const cc = canvas(W, H);
  const { g } = cc;
  g.clearRect(0, 0, W, H);
  const pxm = W / 14;
  for (let x = 0.6 * pxm; x < W - 0.5 * pxm; x += 1.1 * pxm) {
    g.fillStyle = `rgba(230,230,222,${0.75 + rng() * 0.2})`;
    g.fillRect(x, 6, 0.55 * pxm, H - 12);
  }
  // wear
  const img = g.getImageData(0, 0, W, H);
  const d = img.data;
  for (let i = 3; i < d.length; i += 4) if (d[i] > 0) d[i] = Math.max(0, d[i] - rng() * 90);
  g.putImageData(img, 0, 0);
  return toTexture(cc, { repeat: false });
}

export function makeSidewalk(rng: Rng): THREE.Texture {
  const W = 512;
  const cc = canvas(W, W);
  const { g } = cc;
  g.fillStyle = rgb(163, 160, 152);
  g.fillRect(0, 0, W, W);
  noise(cc, 7, rng, 1);
  noise(cc, 5, rng, 16);
  // 4 m tile -> 1 m slabs
  g.fillStyle = 'rgba(0,0,0,0.22)';
  for (let i = 0; i <= 4; i++) {
    g.fillRect((i * W) / 4 - 1, 0, 2, W);
    g.fillRect(0, (i * W) / 4 - 1, W, 2);
  }
  g.fillStyle = 'rgba(255,255,255,0.12)';
  for (let i = 0; i < 4; i++) {
    g.fillRect((i * W) / 4 + 1, 0, 1, W);
    g.fillRect(0, (i * W) / 4 + 1, W, 1);
  }
  return toTexture(cc);
}

export function makeGrass(rng: Rng): THREE.Texture {
  const W = 512;
  const cc = canvas(W, W);
  const { g } = cc;
  g.fillStyle = rgb(78, 104, 56);
  g.fillRect(0, 0, W, W);
  for (let i = 0; i < 40; i++) {
    g.fillStyle = `rgba(${90 + rng() * 40},${110 + rng() * 30},${50 + rng() * 20},0.35)`;
    g.beginPath();
    g.ellipse(rng() * W, rng() * W, 20 + rng() * 60, 15 + rng() * 40, rng() * 3, 0, Math.PI * 2);
    g.fill();
  }
  noise(cc, 10, rng, 1);
  noise(cc, 8, rng, 4);
  return toTexture(cc);
}

export function makeRoof(rng: Rng): THREE.Texture {
  const W = 512;
  const cc = canvas(W, W);
  const { g } = cc;
  g.fillStyle = rgb(70, 69, 72);
  g.fillRect(0, 0, W, W);
  noise(cc, 9, rng, 1);
  noise(cc, 7, rng, 5);
  for (let i = 0; i < 12; i++) {
    g.fillStyle = `rgba(255,255,255,${0.04 + rng() * 0.06})`;
    g.fillRect(rng() * W, rng() * W, 20 + rng() * 80, 4 + rng() * 10);
  }
  return toTexture(cc);
}

export function makeManhole(): THREE.Texture {
  const W = 128;
  const cc = canvas(W, W);
  const { g } = cc;
  g.clearRect(0, 0, W, W);
  g.fillStyle = '#2c2c2e';
  g.beginPath();
  g.arc(64, 64, 60, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = '#4a4a4c';
  g.lineWidth = 4;
  g.beginPath();
  g.arc(64, 64, 52, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 3;
  for (let i = -3; i <= 3; i++) {
    g.beginPath();
    g.moveTo(64 + i * 12, 20);
    g.lineTo(64 + i * 12, 108);
    g.stroke();
  }
  return toTexture(cc, { repeat: false });
}

export function makeLightPool(): THREE.Texture {
  const W = 256;
  const cc = canvas(W, W);
  const { g } = cc;
  g.clearRect(0, 0, W, W);
  const grad = g.createRadialGradient(128, 128, 4, 128, 128, 126);
  grad.addColorStop(0, 'rgba(255,214,150,0.9)');
  grad.addColorStop(0.35, 'rgba(255,200,130,0.4)');
  grad.addColorStop(1, 'rgba(255,190,120,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, W, W);
  return toTexture(cc, { repeat: false });
}

export function makeWaterNoise(): THREE.Texture {
  const W = 256;
  const cc = canvas(W, W);
  const img = cc.g.createImageData(W, W);
  const d = img.data;
  for (let y = 0; y < W; y++) {
    for (let x = 0; x < W; x++) {
      const u = (x / W) * Math.PI * 2;
      const v = (y / W) * Math.PI * 2;
      const a = Math.sin(u * 3 + Math.sin(v * 2) * 1.5) + Math.sin(v * 5 + Math.cos(u * 3)) * 0.7 + Math.sin(u * 7 + v * 4) * 0.4;
      const b = Math.cos(v * 4 + Math.sin(u * 3) * 1.2) + Math.sin(u * 6 - v * 2) * 0.6 + Math.cos(u * 2 + v * 8) * 0.4;
      const i = (y * W + x) * 4;
      d[i] = 128 + a * 40;
      d[i + 1] = 128 + b * 40;
      d[i + 2] = 255;
      d[i + 3] = 255;
    }
  }
  cc.g.putImageData(img, 0, 0);
  const t = toTexture(cc, { srgb: false });
  return t;
}

export function makeMarketRoof(): THREE.Texture {
  const W = 512;
  const cc = canvas(W, W);
  const { g } = cc;
  const cols = ['#2f7d4f', '#c9a227', '#7a2434', '#2c4a7a', '#e0d8c0'];
  const n = 8;
  const s = W / n;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      g.fillStyle = cols[(x + y * 3) % cols.length];
      g.fillRect(x * s, y * s, s, s);
      g.fillStyle = cols[(x * 2 + y + 2) % cols.length];
      g.beginPath();
      g.moveTo(x * s + s / 2, y * s);
      g.lineTo(x * s + s, y * s + s / 2);
      g.lineTo(x * s + s / 2, y * s + s);
      g.lineTo(x * s, y * s + s / 2);
      g.closePath();
      g.fill();
    }
  }
  g.strokeStyle = 'rgba(0,0,0,0.25)';
  g.lineWidth = 2;
  for (let i = 0; i <= n; i++) {
    g.beginPath();
    g.moveTo(i * s, 0);
    g.lineTo(i * s, W);
    g.stroke();
  }
  return toTexture(cc);
}

// ------------------------------------------------------------ text atlases ----

export interface AtlasEntry {
  /** uv rect */
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  aspect: number;
}
export interface TextAtlas {
  map: THREE.Texture;
  entries: AtlasEntry[];
}

const BILLBOARDS: { bg: string; fg: string; a: string; b: string }[] = [
  { bg: '#10224a', fg: '#ffd53a', a: 'DANUBE FM 98.6', b: 'the sound of the river' },
  { bg: '#b8102a', fg: '#fff5e0', a: 'PÁLINKA', b: 'straight from the orchard' },
  { bg: '#f2c400', fg: '#111111', a: '6x6 TAXI', b: 'three minutes, anywhere' },
  { bg: '#1c5b33', fg: '#eef6df', a: 'VISIT THE BUDA HILLS', b: 'fresh air, ten minutes away' },
  { bg: '#0f3f63', fg: '#f6d98a', a: 'THERMAL BATHS', b: 'relax like a king' },
  { bg: '#151515', fg: '#ff8a1f', a: "ZOLI'S GARAGE", b: 'we fix anything. no questions.' },
  { bg: '#4a1a6b', fg: '#ffffff', a: 'PEST BASS 104.2', b: 'all bass, all night' },
  { bg: '#e8e2d2', fg: '#7a2434', a: 'GOULASH KING', b: 'open 24 hours' },
];

export function makeBillboardAtlas(): TextAtlas {
  const W = 2048;
  const H = 1024;
  const cc = canvas(W, H);
  const { g } = cc;
  const entries: AtlasEntry[] = [];
  const cw = 1024;
  const ch = 256;
  BILLBOARDS.forEach((bb, i) => {
    const x = (i % 2) * cw;
    const y = Math.floor(i / 2) * ch;
    g.fillStyle = bb.bg;
    g.fillRect(x, y, cw, ch);
    g.fillStyle = 'rgba(255,255,255,0.06)';
    g.fillRect(x, y, cw, ch * 0.5);
    g.fillStyle = bb.fg;
    g.font = 'bold 96px sans-serif';
    g.textAlign = 'left';
    g.textBaseline = 'middle';
    g.fillText(bb.a, x + 40, y + ch * 0.4);
    g.font = 'italic 44px sans-serif';
    g.fillText(bb.b, x + 44, y + ch * 0.78);
    g.strokeStyle = 'rgba(0,0,0,0.4)';
    g.lineWidth = 8;
    g.strokeRect(x + 4, y + 4, cw - 8, ch - 8);
    entries.push({ u0: x / W, v0: 1 - (y + ch) / H, u1: (x + cw) / W, v1: 1 - y / H, aspect: cw / ch });
  });
  return { map: toTexture(cc, { repeat: false }), entries };
}

export interface SignSpec {
  text: string;
  color: string;
  bg?: string;
  glow?: boolean;
}

/** rows of text signs; each row 1024 x 128 px (aspect 8:1). Transparent background when bg is undefined. */
export function makeSignAtlas(signs: SignSpec[]): TextAtlas {
  const W = 1024;
  const rowH = 128;
  const H = Math.max(128, 1 << Math.ceil(Math.log2(signs.length * rowH)));
  const cc = canvas(W, H);
  const { g } = cc;
  g.clearRect(0, 0, W, H);
  const entries: AtlasEntry[] = [];
  signs.forEach((s, i) => {
    const y = i * rowH;
    if (s.bg) {
      g.fillStyle = s.bg;
      g.fillRect(0, y, W, rowH);
    }
    g.save();
    g.font = `bold 92px sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    // fit text
    let size = 92;
    while (g.measureText(s.text).width > W - 80 && size > 30) {
      size -= 4;
      g.font = `bold ${size}px sans-serif`;
    }
    if (s.glow) {
      g.shadowColor = s.color;
      g.shadowBlur = 28;
      g.fillStyle = s.color;
      g.fillText(s.text, W / 2, y + rowH / 2);
      g.fillText(s.text, W / 2, y + rowH / 2);
      g.shadowBlur = 0;
      g.fillStyle = '#ffffff';
      g.globalAlpha = 0.85;
      g.fillText(s.text, W / 2, y + rowH / 2);
    } else {
      g.fillStyle = s.color;
      g.fillText(s.text, W / 2, y + rowH / 2);
    }
    g.restore();
    entries.push({ u0: 0, v0: 1 - (y + rowH) / H, u1: 1, v1: 1 - y / H, aspect: W / rowH });
  });
  return { map: toTexture(cc, { repeat: false }), entries };
}

// ----------------------------------------------------------------- bundle ----

export interface Textures {
  facades: Record<string, FacadeStyle>;
  shop: FacadeStyle;
  road: THREE.Texture;
  asphalt: THREE.Texture;
  crosswalk: THREE.Texture;
  sidewalk: THREE.Texture;
  grass: THREE.Texture;
  roof: THREE.Texture;
  manhole: THREE.Texture;
  lightPool: THREE.Texture;
  waterNoise: THREE.Texture;
  marketRoof: THREE.Texture;
  billboards: TextAtlas;
  neon: TextAtlas;
  signs: TextAtlas;
}

export const NEON_SIGNS: SignSpec[] = [
  { text: 'KERT BAR', color: '#ff2d7a', glow: true },
  { text: 'ROMKOCSMA', color: '#19e0ff', glow: true },
  { text: 'OPEN', color: '#ff8c1a', glow: true },
  { text: 'COCKTAILS', color: '#3dff7a', glow: true },
  { text: 'PÁLINKA', color: '#ff3b3b', glow: true },
  { text: 'DISCO', color: '#b44dff', glow: true },
  { text: 'LIVE MUSIC', color: '#ffe23a', glow: true },
  { text: 'BEER GARDEN', color: '#4da6ff', glow: true },
];

export const LANDMARK_SIGNS: SignSpec[] = [
  { text: 'HOSPITAL', color: '#ffffff', bg: '#c8102e' },
  { text: 'POLICE', color: '#ffffff', bg: '#1245a8' },
  { text: "ZOLI'S GARAGE", color: '#ff8a1f', bg: '#151515' },
  { text: 'KELETI STATION', color: '#f6e7c1', bg: '#3a2a1a' },
  { text: 'GREAT MARKET HALL', color: '#f4ecd8', bg: '#1f4d2e' },
  { text: 'THERMAL BATH', color: '#f6d98a', bg: '#0f3f63' },
  { text: 'PEST ARENA', color: '#ffffff', bg: '#a4102a' },
  { text: 'DANUBE DOCKS', color: '#ffd53a', bg: '#1a1a1a' },
];

export function makeTextures(seed: number): Textures {
  const rng = makeRng((seed ^ 0x9e3779b9) >>> 0);
  const facades: Record<string, FacadeStyle> = {};
  const add = (s: FacadeSpec) => (facades[s.key] = makeFacade(s, rng));
  add({
    key: 'classic',
    bays: 8,
    floors: 6,
    bayW: 3.6,
    floorH: 3.4,
    wall: [214, 190, 140],
    frame: '#f4f0e6',
    glass: '#6f8497',
    winW: 1.3,
    winH: 2.3,
    winTop: 'flat',
    sill: true,
    band: [232, 222, 196],
    litP: 0.32,
    tints: [0xffffff, 0xf2e0c0, 0xe8d3a8, 0xffe9c9, 0xd9c9b0, 0xf7d9b5],
  });
  add({
    key: 'terracotta',
    bays: 8,
    floors: 6,
    bayW: 3.4,
    floorH: 3.4,
    wall: [178, 108, 76],
    frame: '#efe6d6',
    glass: '#6a7f92',
    winW: 1.2,
    winH: 2.4,
    winTop: 'round',
    sill: true,
    band: [200, 150, 110],
    ornament: true,
    balcony: 0.2,
    litP: 0.3,
    tints: [0xffffff, 0xf0c8b0, 0xd8b8a0, 0xffd6c0],
  });
  add({
    key: 'modern',
    bays: 6,
    floors: 6,
    bayW: 2.0,
    floorH: 3.6,
    wall: [70, 92, 112],
    frame: '#1b2028',
    glass: '#8fb3cc',
    winW: 1.6,
    winH: 2.6,
    winTop: 'flat',
    curtain: true,
    litP: 0.45,
    tints: [0xffffff, 0xd8e4ee, 0xbcd2e2, 0xffffff],
  });
  add({
    key: 'brick',
    bays: 8,
    floors: 6,
    bayW: 3.0,
    floorH: 3.0,
    wall: [146, 78, 58],
    frame: '#e6dfd0',
    glass: '#65788a',
    winW: 1.1,
    winH: 1.6,
    winTop: 'flat',
    brick: true,
    sill: true,
    litP: 0.3,
    tints: [0xffffff, 0xe0c8c0, 0xffd0c0, 0xc8b0a8],
  });
  add({
    key: 'panel',
    bays: 8,
    floors: 6,
    bayW: 3.2,
    floorH: 2.8,
    wall: [168, 166, 158],
    frame: '#3a3a3a',
    glass: '#6a7c8c',
    winW: 1.7,
    winH: 1.4,
    winTop: 'flat',
    panel: true,
    balcony: 0.45,
    litP: 0.35,
    tints: [0xffffff, 0xd8dcc8, 0xd0c8c0, 0xc8d0d8],
  });
  add({
    key: 'gothic',
    bays: 6,
    floors: 4,
    bayW: 4.0,
    floorH: 5.0,
    wall: [226, 214, 190],
    frame: '#efe8da',
    glass: '#3c4a5c',
    winW: 1.6,
    winH: 3.4,
    winTop: 'point',
    pilaster: true,
    band: [236, 226, 204],
    litP: 0.55,
    tints: [0xffffff],
  });
  add({
    key: 'white',
    bays: 6,
    floors: 6,
    bayW: 4.0,
    floorH: 3.4,
    wall: [232, 232, 228],
    frame: '#8a9aa8',
    glass: '#7d97ad',
    winW: 2.4,
    winH: 1.6,
    winTop: 'flat',
    band: [214, 214, 210],
    litP: 0.5,
    tints: [0xffffff],
  });
  add({
    key: 'stone',
    bays: 6,
    floors: 4,
    bayW: 4.0,
    floorH: 4.2,
    wall: [200, 194, 178],
    frame: '#ece6d8',
    glass: '#4c5a6a',
    winW: 1.8,
    winH: 2.8,
    winTop: 'round',
    pilaster: true,
    band: [220, 214, 196],
    sill: true,
    litP: 0.5,
    tints: [0xffffff, 0xf4e8c8],
  });
  return {
    facades,
    shop: makeShopStrip(rng),
    road: makeAsphaltRoad(rng),
    asphalt: makeAsphaltPlain(rng),
    crosswalk: makeCrosswalk(rng),
    sidewalk: makeSidewalk(rng),
    grass: makeGrass(rng),
    roof: makeRoof(rng),
    manhole: makeManhole(),
    lightPool: makeLightPool(),
    waterNoise: makeWaterNoise(),
    marketRoof: makeMarketRoof(),
    billboards: makeBillboardAtlas(),
    neon: makeSignAtlas(NEON_SIGNS),
    signs: makeSignAtlas(LANDMARK_SIGNS),
  };
}
